/**
 * Mock data for the v0 mock-frontend.
 * When MSW is wired in (Phase 0.5), these become responses to fetched endpoints.
 */

export type Business = {
  id: string;
  name: string;
  subtitle: string;
  /** Real businesses.status enum (see api businesses.ts); 'trial' is a legacy/mock alias. */
  status: 'pending_payment' | 'provisioning' | 'active' | 'past_due' | 'suspended' | 'canceled' | 'deleted' | 'trial';
  plan: string;
  isPro: boolean;
  iconColor: string; // Tailwind color shorthand
  /** 'managed' | 'shared' | 'legacy' | 'self_hosted' — only a managed business has a
   *  database of its own to export. Optional because the mock fixtures predate it. */
  dbMode?: string | null;
};

export const currentBusiness: Business = {
  id: 'biz_42',
  name: 'Khan Bakery',
  subtitle: 'Sylhet, BD',
  status: 'active',
  plan: 'Pro',
  isPro: true,
  iconColor: 'bg-zinc-950 dark:bg-zinc-50',
};

export const allBusinesses: Business[] = [
  currentBusiness,
  {
    id: 'biz_43',
    name: 'Khan Bakery — Dhaka',
    subtitle: 'Dhaka, BD',
    status: 'trial',
    plan: 'Pro',
    isPro: true,
    iconColor: 'bg-amber-500',
  },
  {
    id: 'biz_44',
    name: 'Khan Catering',
    subtitle: 'Sylhet, BD',
    status: 'active',
    plan: 'Basic',
    isPro: false,
    iconColor: 'bg-emerald-600',
  },
];

export type Activity = {
  id: string;
  date: string; // ISO
  description: string;
  amount?: number;
  meta?: string;
};

export const recentActivity: Activity[] = [
  { id: 'a1', date: '2026-05-02T14:32:00Z', description: 'Sale completed · invoice #4521', amount: 85000, meta: 'Cash' },
  { id: 'a2', date: '2026-05-02T13:45:00Z', description: 'Stock added · Flour 50kg ×4', amount: -240000 },
  { id: 'a3', date: '2026-05-02T11:20:00Z', description: 'Cashier Rina clocked in', meta: 'Till #2' },
  { id: 'a4', date: '2026-05-02T09:00:00Z', description: 'Daily sales report emailed', meta: 'auto' },
  { id: 'a5', date: '2026-05-01T17:54:00Z', description: 'Sale completed · invoice #4520', amount: 32700 },
];

export type Invoice = {
  id: string;
  number: string;
  date: string;
  amount: number;
  /**
   * Raw payment-provider slug of the settling payment (`sslcommerz | manual_cash
   * | bkash | nagad | bank_transfer | stripe | paypal`), or null when the invoice
   * has no succeeded payment yet. Map to a display label with providerLabel().
   */
  method: string | null;
  /** Real Postgres invoice-status enum (see api money.ts INVOICE_STATUS). */
  status: 'draft' | 'open' | 'paid' | 'partial_refunded' | 'refunded' | 'uncollectible' | 'void' | 'past_due';
  businessId: string;
};

/**
 * Per-business invoice ledger. The user owns multiple businesses, so every
 * invoice carries the owning business id; pages that scope to the active
 * business filter on `currentBusiness.id`, while account-wide views can show
 * all of them with a business column.
 */
export const invoicesByBusiness: Record<string, Invoice[]> = {
  biz_42: [
    { id: 'i1', number: 'INV-2026-0345', date: '2026-05-03', amount: 150000, method: null, status: 'open', businessId: 'biz_42' },
    { id: 'i2', number: 'INV-2026-0312', date: '2026-04-03', amount: 150000, method: 'sslcommerz', status: 'paid', businessId: 'biz_42' },
    { id: 'i3', number: 'INV-2026-0276', date: '2026-03-03', amount: 150000, method: 'manual_cash', status: 'paid', businessId: 'biz_42' },
    { id: 'i4', number: 'INV-2026-0244', date: '2026-02-03', amount: 150000, method: 'sslcommerz', status: 'paid', businessId: 'biz_42' },
  ],
  biz_43: [
    { id: 'i5', number: 'INV-2026-0367', date: '2026-05-09', amount: 0, method: null, status: 'draft', businessId: 'biz_43' },
  ],
  biz_44: [
    { id: 'i6', number: 'INV-2026-0349', date: '2026-05-04', amount: 50000, method: 'sslcommerz', status: 'paid', businessId: 'biz_44' },
    { id: 'i7', number: 'INV-2026-0308', date: '2026-04-04', amount: 50000, method: 'sslcommerz', status: 'paid', businessId: 'biz_44' },
    { id: 'i8', number: 'INV-2026-0263', date: '2026-03-04', amount: 50000, method: 'bank_transfer', status: 'paid', businessId: 'biz_44' },
  ],
};

/** Flat list across all of the user's businesses — for "All businesses" billing view. */
export const allInvoices: Invoice[] = Object.values(invoicesByBusiness)
  .flat()
  .sort((a, b) => (a.date < b.date ? 1 : -1));

/** Backwards-compat: invoices scoped to currentBusiness. */
export const recentInvoices: Invoice[] = invoicesByBusiness['biz_42']!;

export type Subscription = {
  businessId: string;
  plan: string;
  amount: number;
  interval: 'month' | 'year';
  status: 'active' | 'trial' | 'past_due' | 'suspended' | 'canceled';
  startedAt: string;
  renewsAt: string;
  trialEndsAt: string | null;
  usage: {
    locations: { used: number; limit: number };
    users: { used: number; limit: number };
    products: { used: number; limit: number };
  };
  /** Real frozen entitlements from the package snapshot (caps null = unlimited). */
  entitlements?: {
    maxLocations: number | null;
    maxUsers: number | null;
    maxProducts: number | null;
    maxInvoices: number | null;
    maxInvoicesYearly: number | null;
    modules: string[];
  };
  /** Most recent settled payment (there is no saved-card/auto-charge yet). */
  lastPayment?: { provider: string; amountMinor: number; paidAt: string | null } | null;
  /**
   * What is going to happen to this business, and when, while an invoice is unpaid.
   *
   * Computed SERVER-side from the oldest open invoice and the same MILESTONES ladder the
   * dunning cron enforces (api/src/billing/dunning.ts). Never re-derive these dates in the
   * UI: the schedule is policy, and a "day 8" restated in a component drifts the moment the
   * ladder changes. Null when nothing is owed.
   */
  dunning?: {
    invoiceNo: string;
    /** 'YYYY-MM-DD' */
    dueAt: string;
    amountDueMinor: number;
    /** Negative before the due date. */
    daysOverdue: number;
    /** 'YYYY-MM-DD' POS access is suspended if still unpaid. */
    suspendsOn: string;
    /** 'YYYY-MM-DD' the subscription is canceled if it stays unpaid. */
    cancelsOn: string;
    daysUntilSuspend: number;
    suspended: boolean;
  } | null;
};

/**
 * Per-business subscription map. One row per business — Stripe-style: a
 * subscription belongs to a business, not to a user account.
 */
export const subscriptionsByBusiness: Record<string, Subscription> = {
  biz_42: {
    businessId: 'biz_42',
    plan: 'Pro',
    amount: 150000,
    interval: 'month',
    status: 'active',
    startedAt: '2026-04-25',
    renewsAt: '2026-06-03',
    trialEndsAt: null,
    usage: {
      locations: { used: 3, limit: 5 },
      users: { used: 8, limit: 15 },
      products: { used: 1243, limit: 10000 },
    },
  },
  biz_43: {
    businessId: 'biz_43',
    plan: 'Pro',
    amount: 150000,
    interval: 'month',
    status: 'trial',
    startedAt: '2026-05-02',
    renewsAt: '2026-05-09',
    trialEndsAt: '2026-05-09',
    usage: {
      locations: { used: 1, limit: 5 },
      users: { used: 2, limit: 15 },
      products: { used: 64, limit: 10000 },
    },
  },
  biz_44: {
    businessId: 'biz_44',
    plan: 'Basic',
    amount: 50000,
    interval: 'month',
    status: 'active',
    startedAt: '2025-12-01',
    renewsAt: '2026-06-04',
    trialEndsAt: null,
    usage: {
      locations: { used: 1, limit: 2 },
      users: { used: 3, limit: 5 },
      products: { used: 287, limit: 2000 },
    },
  },
};

/** Backwards-compat: subscription scoped to currentBusiness. */
export const subscription = subscriptionsByBusiness['biz_42']!;

export type Kpis = {
  sales: number;
  orders: number;
  cashCollected: number;
  averageOrder: number;
  /** Day-over-day % change. OPTIONAL and absent from the real API today: the POS mirror
   * carries only today's figures, so there is nothing to compare. The tile shows no
   * delta line when these are missing — never a made-up 0.0%. */
  salesDelta?: number;
  ordersDelta?: number;
  averageOrderDelta?: number;
  /**
   * The KPI numbers above are MIRRORED from the per-business Laravel POS
   * database into NestJS Postgres `business_metrics` via an HMAC-signed
   * webhook on a 15-minute schedule. The dashboard reads this mirror —
   * it never queries Laravel directly. `mirroredFromPosAt` is the ISO
   * timestamp of the most recent successful mirror.
   */
  mirroredFromPosAt: string;
  posSource: 'managed' | 'self_hosted' | 'legacy';
};

/**
 * Per-business "today" KPIs. Switching the active business in the BusinessSwitcher
 * changes the dashboard numbers — modeled on `business_metrics` in NESTJS_SCHEMA.
 */
export const kpisByBusiness: Record<string, Kpis> = {
  biz_42: {
    sales: 2850000,
    orders: 87,
    cashCollected: 2020000,
    averageOrder: 32700,
    salesDelta: 12.4,
    ordersDelta: 5.1,
    averageOrderDelta: -2.8,
    mirroredFromPosAt: '2026-05-03T14:32:00Z',
    posSource: 'legacy',
  },
  biz_43: {
    sales: 642000,
    orders: 18,
    cashCollected: 410000,
    averageOrder: 35700,
    salesDelta: 0,
    ordersDelta: 0,
    averageOrderDelta: 0,
    mirroredFromPosAt: '2026-05-03T14:30:00Z',
    posSource: 'managed',
  },
  biz_44: {
    sales: 1423000,
    orders: 41,
    cashCollected: 980000,
    averageOrder: 34700,
    salesDelta: -3.2,
    ordersDelta: 2.1,
    averageOrderDelta: -1.5,
    mirroredFromPosAt: '2026-05-03T14:28:00Z',
    posSource: 'self_hosted',
  },
};

/** Backwards-compat alias scoped to current business. */
export const todaysKpis = kpisByBusiness['biz_42']!;

export const currentUser = {
  id: 'usr_10',
  name: 'Imran Khan',
  email: 'imran@khan-bakery.com',
  initials: 'IK',
};

/**
 * Apps catalog (NESTJS_SCHEMA `apps` table) — the three brands ITCity ships
 * Possignin under. A business is created against one app.
 */
export type App = {
  id: string;
  code: 'ogrogoti' | 'pos_signin' | 'poc_signin';
  name: string;
  description: string;
  isPrimary: boolean;
  marketUrl: string;
  primaryColor: string;
};

export const apps: App[] = [
  {
    id: 'app_ogrogoti',
    code: 'ogrogoti',
    name: 'Ogrogoti',
    description: 'The flagship Bangla-first POS — restaurant + retail + pharmacy.',
    isPrimary: true,
    marketUrl: 'https://ogrogoti.com',
    primaryColor: '#4F46E5',
  },
  {
    id: 'app_pos_signin',
    code: 'pos_signin',
    name: 'POS Signin',
    description: 'English-localized variant for shops in larger metro markets.',
    isPrimary: false,
    marketUrl: 'https://saas.itcitysoft.com',
    primaryColor: '#0284C7',
  },
  {
    id: 'app_poc_signin',
    code: 'poc_signin',
    name: 'POC Signin',
    description: 'Lightweight tier for proof-of-concept and small pilots.',
    isPrimary: false,
    marketUrl: 'https://pocsignin.com',
    primaryColor: '#059669',
  },
];
